// components/HealthStatusPage.js
import { useMemo, useState } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useHealth } from "../contexts/HealthContext";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const HealthStatusPage = () => {
    const { healthHistory, loadingHealthHistory, modelsUsed, stepFunctionErrorHistoryCount } = useHealth();
    const fixedTaxonomyEnabled = window.appSettings?.fixedTaxonomyEnabled;

    const [selectedChart, setSelectedChart] = useState('records');
    const [showHistoryTable, setShowHistoryTable] = useState(false);

    // Get the most recent health record
    const currentHealth = healthHistory[0];

    // Determine overall health status
    let isOk = currentHealth && currentHealth.recs_ready_1h > 0 && stepFunctionErrorHistoryCount === 0;
    if (isOk && fixedTaxonomyEnabled && currentHealth?.recs_fixedintent_1h < 1) {
        isOk = false;
    }

    let isWarning = false;
    if (currentHealth && !isOk && currentHealth.recs_ready_24h > 0) {
        if (fixedTaxonomyEnabled) {
            isWarning = currentHealth.recs_fixedintent_24h > 0;
        } else {
            isWarning = true;
        }
    }

    const apiErrorRate24h = currentHealth?.api_called_24h > 0
        ? ((currentHealth.api_errors_24h / currentHealth.api_called_24h) * 100).toFixed(1)
        : "0";

    // Calculate processing percentages
    const processedPercentage1h = currentHealth?.recs_new_1h > 0
        ? ((currentHealth.recs_ready_1h / currentHealth.recs_new_1h) * 100).toFixed(0)
        : "0";

    const processedPercentage24h = currentHealth?.recs_new_24h > 0
        ? ((currentHealth.recs_ready_24h / currentHealth.recs_new_24h) * 100).toFixed(0)
        : "0";

    // Calculate fixed intent processing percentages
    const fixedIntentPercentage1h = currentHealth?.recs_new_1h > 0
        ? ((currentHealth.recs_fixedintent_1h / currentHealth.recs_new_1h) * 100).toFixed(0)
        : "0";

    const fixedIntentPercentage24h = currentHealth?.recs_new_24h > 0
        ? ((currentHealth.recs_fixedintent_24h / currentHealth.recs_new_24h) * 100).toFixed(0)
        : "0";

    // Health status color and text
    const statusColor = isOk ? "bg-green-500" : (isWarning ? "bg-yellow-500" : "bg-red-500");
    const statusText = isOk ? "Healthy" : (isWarning ? "Warning" : "Critical");

    // Calculate time since last update
    const getTimeSinceUpdate = () => {
        if (!currentHealth?.health_date) return "N/A";
        const now = new Date();
        const updateTime = new Date(currentHealth.health_date);
        const diffMs = now - updateTime;
        const diffMinutes = Math.floor(diffMs / 60000);

        if (diffMinutes < 60) return `${diffMinutes}m ago`;
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}d ago`;
    };

    // Format large numbers
    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    // Calculate totals
    const totals = useMemo(() => {
        if (!healthHistory.length) return {};
        return {
            totalRecordsProcessed: healthHistory.reduce((sum, h) => sum + (h.recs_ready_24h || 0), 0),
            totalIntents: currentHealth?.new_majorintents_24h || 0,
            totalCategories: currentHealth?.new_categories_24h || 0,
        };
    }, [healthHistory, currentHealth]);

    // MetricCard Component with Tippy integration
    const MetricCard = ({ title, value, subtitle, trend, color = "blue", large = false, tooltip }) => {
        const bgColors = {
            blue: "bg-blue-50 border-blue-200",
            green: "bg-green-50 border-green-200",
            red: "bg-red-50 border-red-200",
            yellow: "bg-yellow-50 border-yellow-200",
            purple: "bg-purple-50 border-purple-200",
            teal: "bg-teal-50 border-teal-200",
        };

        const card = (
            <div className={`${bgColors[color]} border rounded-lg p-4 ${large ? 'col-span-2' : ''} text-center cursor-help`}>
                <div className="text-sm text-gray-600 mb-1">{title}</div>
                <div className={`font-bold ${large ? 'text-3xl' : 'text-2xl'}`}>{value}</div>
                {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
                {trend !== undefined && (
                    <div className={`text-xs mt-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}%
                    </div>
                )}
            </div>
        );

        return tooltip ? (
            <Tippy content={tooltip} placement="top" arrow>
                {card}
            </Tippy>
        ) : card;
    };

    // AG Grid column definitions
    const columnDefs = useMemo(() => {
        if (!healthHistory || healthHistory.length === 0) return [];
        const sampleData = healthHistory[0];
        return Object.keys(sampleData).map(key => ({
            headerName: key,
            field: key,
            sortable: true,
            filter: true,
            resizable: true,
            width: 120, // Reduce column width
            valueFormatter: key.includes('date') ?
                (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A'
                : undefined
        }));
    }, [healthHistory]);

    // Prepare chart data for different metric groups
    const prepareChartData = (metricGroup) => {
        if (!healthHistory || healthHistory.length === 0) return null;

        const sortedData = [...healthHistory].sort((a, b) => new Date(a.health_date) - new Date(b.health_date));

        const metricsConfig = {
            records: [
                { key: 'recs_new_1h', label: 'New (1h)', color: 'rgb(59, 130, 246)' },
                { key: 'recs_new_24h', label: 'New (24h)', color: 'rgb(147, 197, 253)' },
                { key: 'recs_ready_1h', label: 'Ready (1h)', color: 'rgb(34, 197, 94)' },
                { key: 'recs_ready_24h', label: 'Ready (24h)', color: 'rgb(134, 239, 172)' },
            ],
            api: [
                { key: 'api_called_24h', label: 'API Calls (24h)', color: 'rgb(94, 234, 212)' },
                { key: 'api_errors_24h', label: 'API Errors (24h)', color: 'rgb(252, 165, 165)' },
            ],
            intents: [
                { key: 'new_majorintents_1h', label: 'Major Intents (1h)', color: 'rgb(245, 158, 11)' },
                { key: 'new_majorintents_24h', label: 'Major Intents (24h)', color: 'rgb(252, 211, 77)' },
                { key: 'new_categories_1h', label: 'Categories (1h)', color: 'rgb(139, 69, 19)' },
                { key: 'new_categories_24h', label: 'Categories (24h)', color: 'rgb(160, 82, 45)' },
            ]
        };

        const selectedMetrics = metricsConfig[metricGroup] || [];

        const datasets = selectedMetrics.map(metric => ({
            label: metric.label,
            data: sortedData.map(record => ({
                x: new Date(record.health_date).getTime(),
                y: record[metric.key] || 0,
                originalDate: record.health_date
            })),
            borderColor: metric.color,
            backgroundColor: metric.color + '20',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
        }));

        return {
            datasets
        };
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    boxWidth: 12,
                    padding: 10,
                    font: { size: 11 }
                }
            },
            tooltip: {
                mode: 'point',
                intersect: false,
                callbacks: {
                    title: function (context) {
                        const dataPoint = context[0].raw;
                        return new Date(dataPoint.originalDate).toLocaleString();
                    },
                    label: function (context) {
                        return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'linear',
                position: 'bottom',
                ticks: {
                    maxTicksLimit: 10,
                    callback: function (value) {
                        return new Date(value).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            hour12: true
                        });
                    }
                }
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        return value.toLocaleString();
                    }
                }
            }
        }
    };

    const recordsChartData = prepareChartData('records');
    const apiChartData = prepareChartData('api');
    const intentsChartData = prepareChartData('intents');

    return (
        <div className="p-4 max-w-7xl mx-auto">
            {/* Header with Status */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Health Dashboard</h2>
                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">Updated {getTimeSinceUpdate()}</span>
                    <div className="flex items-center">
                        <div className={`w-3 h-3 ${statusColor} rounded-full mr-2 animate-pulse`}></div>
                        <span className="font-medium">{statusText}</span>
                    </div>
                </div>
            </div>

            {loadingHealthHistory ? (
                <div className="flex items-center justify-center h-64">
                    <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        <MetricCard
                            title="Records/Hour"
                            value={
                                <div>
                                    <span className="font-bold text-2xl">{processedPercentage1h}%</span>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {formatNumber(currentHealth?.recs_ready_1h)} / {formatNumber(currentHealth?.recs_new_1h)}
                                    </div>
                                </div>
                            }
                            color="green"
                            tooltip="Percentage of new records that were successfully processed (ready) in the last hour"
                        />
                        <MetricCard
                            title="Records/24h"
                            value={
                                <div>
                                    <span className="font-bold text-2xl">{processedPercentage24h}%</span>
                                    <div className="text-xs text-gray-500 mt-1">
                                        {formatNumber(currentHealth?.recs_ready_24h)} / {formatNumber(currentHealth?.recs_new_24h)}
                                    </div>
                                </div>
                            }
                            color="green"
                            tooltip="Percentage of new records that were successfully processed (ready) in the last 24 hours"
                        />
                        <MetricCard
                            title="New Intents"
                            value={currentHealth?.new_majorintents_24h || 0}
                            color="blue"
                            tooltip="Number of new major intents discovered in the last 24 hours"
                        />
                        <MetricCard
                            title="New Categories"
                            value={currentHealth?.new_categories_24h || 0}
                            color="blue"
                            tooltip="Number of new categories discovered in the last 24 hours"
                        />
                        <MetricCard
                            title="API Error Rate"
                            value={`${apiErrorRate24h}%`}
                            subtitle="Last 24h"
                            color={parseFloat(apiErrorRate24h) > 0 ? "red" : "green"}
                            tooltip="Percentage of API calls that resulted in errors over the last 24 hours"
                        />
                        <MetricCard
                            title="Step Errors"
                            value={stepFunctionErrorHistoryCount}
                            color={stepFunctionErrorHistoryCount > 0 ? "red" : "green"}
                            tooltip="Number of AWS Step Function execution errors detected"
                        />
                    </div>

                    {/* Processing Status Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Records Processing */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <Tippy content="Shows the percentage of records being processed successfully over different time periods" placement="top" arrow>
                                <h3 className="font-semibold text-sm text-gray-700 mb-3 cursor-help">Records Processing</h3>
                            </Tippy>
                            <div className="space-y-3">
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">1 Hour Processing</span>
                                        <span className="text-lg font-bold">{processedPercentage1h}%</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {formatNumber(currentHealth?.recs_ready_1h)} / {formatNumber(currentHealth?.recs_new_1h)} records
                                    </div>
                                </div>
                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-gray-600">24 Hour Processing</span>
                                        <span className="text-lg font-bold">{processedPercentage24h}%</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {formatNumber(currentHealth?.recs_ready_24h)} / {formatNumber(currentHealth?.recs_new_24h)} records
                                    </div>
                                </div>
                                {fixedTaxonomyEnabled && (
                                    <div className="pt-2 mt-2 border-t">
                                        <div>
                                            <div className="flex justify-between items-center mb-1">
                                                <Tippy content="Percentage of new records that received fixed intent classification in the last hour" placement="left" arrow>
                                                    <span className="text-xs text-gray-600 cursor-help">Fixed Intent Processing (1h)</span>
                                                </Tippy>
                                                <span className="text-lg font-bold ">{fixedIntentPercentage1h}%</span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatNumber(currentHealth?.recs_fixedintent_1h)} / {formatNumber(currentHealth?.recs_new_1h)} records
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <div className="flex justify-between items-center mb-1">
                                                <Tippy content="Percentage of new records that received fixed intent classification in the last 24 hours" placement="left" arrow>
                                                    <span className="text-xs text-gray-600 cursor-help">Fixed Intent Processing (24h)</span>
                                                </Tippy>
                                                <span className="text-lg font-bold ">{fixedIntentPercentage24h}%</span>
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatNumber(currentHealth?.recs_fixedintent_24h)} / {formatNumber(currentHealth?.recs_new_24h)} records
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Error Rates */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <Tippy content="API call statistics and error rates for monitoring system health" placement="top" arrow>
                                <h3 className="font-semibold text-sm text-gray-700 mb-3 cursor-help">Error Rates</h3>
                            </Tippy>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">API Calls (24h)</span>
                                    <span className="text-sm font-medium">
                                        {currentHealth?.api_called_24h}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">API Errors (24h)</span>
                                    <span className={`text-sm font-medium ${currentHealth?.api_errors_24h > 0 ? 'text-red-600' : ''}`}>
                                        {currentHealth?.api_errors_24h}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">API Error Rate</span>
                                    <span className={`text-sm font-medium ${parseFloat(apiErrorRate24h) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {apiErrorRate24h}%
                                    </span>
                                </div>
                                <div className="pt-2 mt-2 border-t">
                                    <div className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600">Step Function Errors</span>
                                        <span className={`text-sm font-medium ${currentHealth?.step_function_errors_24h > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            {currentHealth?.step_function_errors_24h || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Models & System */}
                        <div className="bg-white rounded-lg shadow p-4">
                            <Tippy content="Currently active AWS Bedrock models used for processing" placement="top" arrow>
                                <h3 className="font-semibold text-sm text-gray-700 mb-3 cursor-help">Bedrock Models</h3>
                            </Tippy>
                            <div className="space-y-2">
                                <div className="text-xs mb-1">Active Models</div>
                                {modelsUsed.map((model, idx) => (
                                    <Tippy key={idx} content={model} placement="left" arrow>
                                        <div className="text-xs text-gray-600 p-1 rounded truncate cursor-help">
                                            {model}
                                        </div>
                                    </Tippy>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Charts Section with Tabs */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="border-b border-gray-200 px-4 py-3">
                            <div className="flex items-center justify-between">
                                <Tippy content="Historical trends of key system metrics over time" placement="top" arrow>
                                    <h3 className="text-sm font-semibold cursor-help">Metrics Trends</h3>
                                </Tippy>
                                <div className="flex gap-2">
                                    <Tippy content="View record processing metrics over time" placement="top" arrow>
                                        <button
                                            onClick={() => setSelectedChart('records')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                selectedChart === 'records' 
                                                    ? 'bg-blue-100 text-blue-800' 
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            Records Processing
                                        </button>
                                    </Tippy>
                                    <Tippy content="View API usage and error trends" placement="top" arrow>
                                        <button
                                            onClick={() => setSelectedChart('api')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                selectedChart === 'api' 
                                                    ? 'bg-blue-100 text-blue-800' 
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            API Usage
                                        </button>
                                    </Tippy>
                                    <Tippy content="View intent and category discovery trends" placement="top" arrow>
                                        <button
                                            onClick={() => setSelectedChart('intents')}
                                            className={`px-3 py-1 text-xs rounded-full transition-colors ${
                                                selectedChart === 'intents' 
                                                    ? 'bg-blue-100 text-blue-800' 
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            Intents & Categories
                                        </button>
                                    </Tippy>
                                </div>
                            </div>
                        </div>
                        <div className="p-4">
                            <div style={{ height: '300px' }}>
                                {selectedChart === 'records' && recordsChartData && (
                                    <Line data={recordsChartData} options={chartOptions} />
                                )}
                                {selectedChart === 'api' && apiChartData && (
                                    <Line data={apiChartData} options={chartOptions} />
                                )}
                                {selectedChart === 'intents' && intentsChartData && (
                                    <Line data={intentsChartData} options={chartOptions} />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Collapsible History Table */}
                    <div className="bg-white rounded-lg shadow">
                        <div
                            className="border-b border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-50"
                            onClick={() => setShowHistoryTable(!showHistoryTable)}
                        >
                            <div className="flex items-center justify-between">
                                <Tippy content="Raw historical data table with all metrics" placement="top" arrow>
                                    <h3 className="text-sm font-semibold cursor-help">Detailed History ({healthHistory.length} records)</h3>
                                </Tippy>
                                <svg
                                    className={`w-4 h-4 transition-transform ${showHistoryTable ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                        </div>
                        {showHistoryTable && (
                            <div className="p-4">
                                <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                                    <AgGridReact
                                        rowData={healthHistory}
                                        columnDefs={columnDefs}
                                        pagination={true}
                                        paginationPageSize={100}
                                        animateRows={true}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HealthStatusPage;